import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/apiClient";
import { DatePicker } from "@/components/DatePicker";
import { formatDate } from "@/lib/statuts";
import {
  Route as RouteIcon, MapPin, Clock, Navigation, Loader2, CalendarDays,
  Gauge, Phone, FileText, MessageSquare, ChevronDown, ChevronUp, AlertCircle, Ban, Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function mapsUrl(e) {
  const dest = [e.adresse, e.code_postal, e.ville].filter(Boolean).join(", ");
  const q = dest || (e.latitude != null ? `${e.latitude},${e.longitude}` : "");
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`;
}
function wazeUrl(e) {
  if (e.latitude != null && e.longitude != null) return `https://waze.com/ul?ll=${e.latitude},${e.longitude}&navigate=yes`;
  const dest = [e.adresse, e.code_postal, e.ville].filter(Boolean).join(", ");
  return `https://waze.com/ul?q=${encodeURIComponent(dest)}&navigate=yes`;
}

function StatutBadge({ e }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold text-white"
      style={{ backgroundColor: e.statut_color || "#94a3b8" }}
      data-testid={`statut-badge-${e.id}`}
    >
      {e.statut_label}
    </span>
  );
}

function RdvCard({ e, index, showOrder = true }) {
  const navigate = useNavigate();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/visites/${e.id}`)}
      onKeyDown={(ev) => { if (ev.key === "Enter") navigate(`/visites/${e.id}`); }}
      data-testid={`rdv-card-${e.id}`}
      className="block cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 shadow-sm active:scale-[0.99] transition-transform"
    >
      <div className="flex items-start gap-3">
        {showOrder && (
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0A0A0A] text-sm font-black text-white">
            {index}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-lg font-black tabular-nums text-slate-900">
              <Clock className="h-4 w-4 text-slate-400" />
              {e.heure_debut || "--:--"}
              {e.heure_fin ? <span className="text-sm font-medium text-slate-400">– {e.heure_fin}</span> : null}
            </div>
            <StatutBadge e={e} />
          </div>

          <div className="mt-1.5 flex items-center gap-2 truncate">
            <span className="truncate text-base font-bold text-slate-900" data-testid={`client-${e.id}`}>{e.client_nom || "Client"}</span>
            {e.numero_dossier ? <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">#{e.numero_dossier}</span> : null}
          </div>

          <div className="mt-1 flex items-start gap-1.5 text-sm text-slate-500">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{[e.adresse, e.complement_adresse].filter(Boolean).join(", ")}<br />{e.code_postal} {e.ville}</span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{e.type_visite}</span>
            {e.type_visite_plus ? <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{e.type_visite_plus}</span> : null}
            {e.distance_precedent_km ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-600">
                <Navigation className="h-3 w-3" />{e.distance_precedent_km} km · ~{e.temps_precedent_min} min
              </span>
            ) : null}
          </div>

          {(e.instructions || e.commentaires) && (
            <div className="mt-2 space-y-1">
              {e.instructions ? (
                <div className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                  <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{e.instructions}</span>
                </div>
              ) : null}
              {e.commentaires ? (
                <div className="flex items-start gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">
                  <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{e.commentaires}</span>
                </div>
              ) : null}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2" onClick={(ev) => ev.stopPropagation()}>
            <a href={mapsUrl(e)} target="_blank" rel="noreferrer" data-testid={`maps-${e.id}`}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white py-2 text-xs font-bold text-slate-700 active:bg-slate-50">
              <MapPin className="h-4 w-4 text-blue-600" /> Plan
            </a>
            <a href={wazeUrl(e)} target="_blank" rel="noreferrer" data-testid={`waze-${e.id}`}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white py-2 text-xs font-bold text-slate-700 active:bg-slate-50">
              <Navigation className="h-4 w-4 text-[#33ccff]" /> Waze
            </a>
            {e.client_telephone ? (
              <a href={`tel:${e.client_telephone}`} data-testid={`tel-${e.id}`}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 active:bg-slate-50">
                <Phone className="h-4 w-4 text-emerald-600" />
              </a>
            ) : null}
            {e.nb_documents ? (
              <span className="flex items-center gap-1 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-500">
                <FileText className="h-4 w-4" />{e.nb_documents}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MaTournee() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnnules, setShowAnnules] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ma-tournee?date=${date}`);
      setData(data);
    } catch (e) { setData(null); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const r = data?.resume;
  const fmtTemps = (min) => {
    if (min == null) return "—";
    const h = Math.floor(min / 60), m = min % 60;
    return h ? `${h} h ${String(m).padStart(2, "0")}` : `${m} min`;
  };

  return (
    <div className="mx-auto max-w-xl space-y-4 pb-20">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RouteIcon className="h-6 w-6 text-[#0A0A0A]" />
          <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900">Ma tournée</h1>
        </div>
        <div className="w-40">
          <DatePicker value={date} onChange={(v) => setDate(v || date)} testid="matournee-date" clearable={false} />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>
      ) : !data ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">Impossible de charger la tournée.</div>
      ) : (
        <>
          {/* Récapitulatif de journée */}
          <div className="rounded-2xl bg-[#0A0A0A] p-5 text-white" data-testid="matournee-resume">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-300">
              <CalendarDays className="h-4 w-4" /> Ma journée — {formatDate(date)}
            </div>
            <div className="mt-3 flex items-end gap-2">
              <span className="font-heading text-5xl font-black leading-none">{r.nb_rdv}</span>
              <span className="mb-1 text-lg font-semibold text-slate-300">RDV</span>
              {data.ordre_source === "tournee" ? (
                <span className="mb-1.5 ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] font-bold text-emerald-300">
                  <Sparkles className="h-3 w-3" /> Ordre optimisé
                </span>
              ) : (
                <span className="mb-1.5 ml-auto inline-flex items-center gap-1 rounded-full bg-slate-700 px-2 py-0.5 text-[11px] font-medium text-slate-300">
                  Ordre par horaire
                </span>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-white/5 p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Premier RDV</div>
                <div className="mt-0.5 text-lg font-black tabular-nums">{r.premier_rdv || "—"}</div>
              </div>
              <div className="rounded-xl bg-white/5 p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Dernier RDV</div>
                <div className="mt-0.5 text-lg font-black tabular-nums">{r.dernier_rdv || "—"}</div>
              </div>
              <div className="rounded-xl bg-white/5 p-3">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-400"><Gauge className="h-3 w-3" /> Distance</div>
                <div className="mt-0.5 text-lg font-black tabular-nums">{r.distance_km} km</div>
              </div>
              <div className="rounded-xl bg-white/5 p-3">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-400"><Clock className="h-3 w-3" /> Trajet</div>
                <div className="mt-0.5 text-lg font-black tabular-nums">{fmtTemps(r.temps_min)}</div>
              </div>
            </div>
            {!data.technicien.has_home ? (
              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-amber-300">
                <AlertCircle className="h-3.5 w-3.5" /> Domicile non renseigné — distance estimée depuis le 1er RDV.
              </div>
            ) : null}
          </div>

          {/* Liste ordonnée */}
          {data.etapes.length === 0 && data.nouveaux.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="matournee-empty">
              <RouteIcon className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-2 text-sm font-medium text-slate-500">Aucun rendez-vous prévu ce jour.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="matournee-etapes">
              {data.etapes.map((e) => <RdvCard key={e.id} e={e} index={e.ordre} />)}
            </div>
          )}

          {/* Nouveaux RDV non positionnés */}
          {data.nouveaux.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 pt-1 text-sm font-bold text-blue-700">
                <AlertCircle className="h-4 w-4" /> Nouveau{data.nouveaux.length > 1 ? "x" : ""} RDV — non positionné{data.nouveaux.length > 1 ? "s" : ""}
              </div>
              <div className="space-y-3" data-testid="matournee-nouveaux">
                {data.nouveaux.map((e) => (
                  <div key={e.id} className="rounded-2xl border-2 border-dashed border-blue-300">
                    <RdvCard e={e} showOrder={false} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Annulés (repliés) */}
          {data.annules.length > 0 && (
            <div className="pt-1">
              <button
                onClick={() => setShowAnnules((v) => !v)}
                data-testid="matournee-toggle-annules"
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-500"
              >
                <span className="flex items-center gap-2"><Ban className="h-4 w-4" /> Annulés ({data.annules.length})</span>
                {showAnnules ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {showAnnules && (
                <div className="mt-3 space-y-3 opacity-70" data-testid="matournee-annules">
                  {data.annules.map((e) => <RdvCard key={e.id} e={e} showOrder={false} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
